<template>
    <div class="starting-next-root">
        <div class="starting-next-bg" :style="bannerStyle"/>
        <div class="starting-next-scroll">
            <div class="starting-next-title-row" ref="titleRow">
                <span class="starting-next-title-pill">{{ $t('journey.starting.title') }}</span>
            </div>
            <v-row class="ma-0 pa-2 starting-next-grid" dense>
                <v-col v-for="tile in tiles" :key="tile.key" :cols="colSpan" class="text-center starting-next-col">
                    <div class="starter-tile" @click="select(tile)">
                        <div class="starter-tile-card">
                            <v-img :src="tile.img" contain :class="{'starter-tile-img--silhouette': tile.isRandom}" class="starter-tile-img"/>
                            <div v-if="tile.isRandom" class="starter-tile-random-mark">?</div>
                        </div>
                        <div class="starter-tile-name">{{ tile.title }}</div>
                    </div>
                </v-col>
            </v-row>
        </div>

        <ResponsiveDialog v-model="showQuickSettings" :max-width="480" :full-height="false" :header-title="$t('journey.starting.quickSettingsTitle')" header-class="skin-window-header--welcome" card-class="skin-card">
            <WelcomeQuickSettings @continue="showQuickSettings = false"/>
        </ResponsiveDialog>

        <ResponsiveDialog v-model="showWelcome" :max-width="480" :full-height="false" :header-title="$t('journey.starting.welcomeTitle')" header-class="skin-window-header--welcome" card-class="skin-card">
            <div class="starting-welcome skin-window--welcome">
                <v-card-text class="text--black font-weight-bold starting-welcome-text">
                    <v-img class="starting-welcome-oak" src="@/assets/pic/web/oak_avatar.png" contain max-width="140"/>
                    <ul>
                        <li>{{ $t('journey.starting.welcomeItem1') }}</li>
                        <li>{{ $t('journey.starting.welcomeItem2') }}</li>
                        <li>{{ $t('journey.starting.welcomeItem3') }}</li>
                        <li>{{ $t('journey.starting.welcomeItem4') }}</li>
                        <li>{{ $t('journey.starting.welcomeItem5') }}</li>
                    </ul>
                </v-card-text>
                <v-card-actions class="justify-center starting-welcome-actions">
                    <v-btn
                        rounded
                        large
                        color="primary black--text"
                        class="btn-shadow"
                        @click="showWelcome = false"
                    >
                        {{ $t('journey.starting.continueButton') }}
                    </v-btn>
                </v-card-actions>
            </div>
        </ResponsiveDialog>

        <ConfirmDialog ref="confirmDialog"/>
    </div>
</template>

<script>
    // Úvodní obrazovka (position.type === START) - výběr startovního pokémona.
    // Vlastní vzhled dlaždic (`starter-tile`), ne sdílená `NextCircleTile` -
    // pokémon sprity mají průhledné pozadí a chtějí se zobrazit celé beze
    // ořezu, na rozdíl od fotek měst/lidí. Design viz claude/components/journey-city.md.
    import {mapActions, mapGetters} from 'vuex';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import ConfirmDialog from '@/components/utils/ConfirmDialog';
    import WelcomeQuickSettings from '@/components/journey/next/pieces/WelcomeQuickSettings';
    import {pokemonImageSrc} from '@/utils/pokemonImage';
    import {setFlag} from '@/config/featureFlags';
    import {computeGridCols} from '@/utils/gridLayout';

    const RANDOM_PICK_ID = 0;

    export default {
        name: 'StartingNext',
        components: {ResponsiveDialog, ConfirmDialog, WelcomeQuickSettings},
        data() {
            return {
                startingPokemons: [],
                // showWelcome začíná na false, odemkne ho až watch níže.
                showQuickSettings: true,
                showWelcome: false,
                containerWidth: 0,
                containerHeight: 0,
                titleRowHeight: 0,
            }
        },
        computed: {
            ...mapGetters({
                user: 'user/item',
            }),
            bannerStyle() {
                return {backgroundImage: `url(${require('@/assets/pic/position/START.png')})`};
            },
            tiles() {
                return this.startingPokemons.map((p) => ({
                    key: p.id,
                    title: p.id === RANDOM_PICK_ID ? this.$t('journey.starting.randomPick') : p.name,
                    img: p.id === RANDOM_PICK_ID
                        ? require('@/assets/pic/pokemons/150.png')
                        : pokemonImageSrc(p.img),
                    isRandom: p.id === RANDOM_PICK_ID,
                    pokemonTypeId: p.id,
                    pokemonName: p.name,
                }));
            },
            // Zkouší od nejmenšího počtu sloupců (2) nahoru - první, kam se
            // celý grid vejde na výšku bez scrollování, vyhrává. Nevejde-li se
            // ani na 4 sloupce, necha se 4 a zbytek dořeší scroll.
            gridCols() {
                return computeGridCols({
                    n: this.tiles.length,
                    containerWidth: this.containerWidth,
                    containerHeight: this.containerHeight,
                    extraHeight: this.titleRowHeight,
                    rowGap: 14,
                    // výška řádku = šířka dlaždice (čtverec) + místo na jméno pod ní
                    nameRowH: 32,
                });
            },
            colSpan() {
                return 12 / this.gridCols;
            },
        },
        // Zavření quick-settings kroku (tlačítkem i backdrop/křížkem) odemkne
        // Oak uvítání a zároveň tiše potlačí error toasty pro nového hráče.
        watch: {
            showQuickSettings(value) {
                if (!value) {
                    this.showWelcome = true;
                    setFlag('notifyErr', false);
                }
            },
        },
        created() {
            this.startingPokemons = [{id: RANDOM_PICK_ID, name: 'Random pick'}];
            this.loadPokemons().then((data) => {
                this.startingPokemons = [].concat(this.startingPokemons, data);
                this.measure();
            });
        },
        mounted() {
            this.measure();
            window.addEventListener('resize', this.measure);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.measure);
        },
        methods: {
            ...mapActions({
                loadPokemons: 'journey/loadStartingPokemons',
                choosePokemon: 'journey/choosePokemon',
            }),
            measure() {
                this.$nextTick(() => {
                    if (!this.$el || !this.$el.getBoundingClientRect) {
                        return;
                    }
                    // `this.$el.clientHeight` je nespolehlivý - rodičovský
                    // <v-card> (Journey.vue) nemá explicitní výšku, takže
                    // "height: 100%" spadne na "auto" (podle obsahu, ne
                    // dostupného prostoru). `getBoundingClientRect().top` +
                    // `window.innerHeight` dá skutečný zbývající prostor
                    // nezávisle na tomhle řetězci.
                    const rect = this.$el.getBoundingClientRect();
                    this.containerWidth = rect.width;
                    this.containerHeight = window.innerHeight - rect.top;
                    if (this.$refs.titleRow) {
                        this.titleRowHeight = this.$refs.titleRow.offsetHeight;
                    }
                });
            },
            async select(tile) {
                const question = tile.pokemonTypeId === RANDOM_PICK_ID
                    ? this.$t('journey.starting.confirmRandom')
                    : this.$t('journey.starting.confirmChoose', {name: tile.pokemonName});
                const opts = {
                    iconButtons: true,
                    pill: true,
                    cardColor: 'amber lighten-3',
                    // pilulka o odstín sytější než karta - stejný princip jako
                    // "vlak" (CityTravelDialog.vue), jen v oranžové rodině.
                    pillColor: '#FFB300',
                    pillShadowColor: 'rgba(255,143,0,0.35)',
                };
                if (await this.$refs.confirmDialog.open(question, opts)) {
                    this.choosePokemon({userId: this.user.id, pokemonTypeId: tile.pokemonTypeId});
                }
            },
        },
    }
</script>

<style scoped>
.starting-next-root {
    position: relative;
    height: 100%;
    overflow: hidden;
}
/* fixed = celý obrázek laboratoře vidíme pořád celý, bez ohledu na scroll obsahu nad ním */
.starting-next-bg {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    z-index: 0;
}
.starting-next-scroll {
    position: relative;
    z-index: 1;
    height: 100%;
    overflow-y: auto;
}
.starting-next-title-row {
    text-align: center;
    padding: 10px 12px 8px 12px;
}
.starting-next-title-pill {
    display: inline-block;
    background: rgba(0,0,0,0.65);
    color: #fff;
    /* 22px se na 360px širokém mobilu zalamovalo na 2 řádky - zbytečně
       "sežralo" výšku, kterou by jinak dynamický výpočet gridCols mohl
       využít pro méně sloupců/větší dlaždice. */
    font-size: 17px;
    font-weight: 700;
    padding: 5px 16px;
    border-radius: 20px;
    text-align: center;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.6);
}
.starting-next-grid {
    row-gap: 14px;
    /* rezerva pro globální TeamBottomSheet (App.vue) */
    padding-bottom: 22px !important;
}
.starting-next-col {
    padding: 0 4px !important;
}
.starter-tile {
    cursor: pointer;
    width: 100%;
    -webkit-tap-highlight-color: transparent;
}
.starter-tile-card {
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    border-radius: 14px;
    /* stejná konvence jako globální třída ".transparent06" v _card.sass, jen
       tady přímo v rgba - jednodušší než řešit pořadí/specificitu */
    background: rgba(255,255,255,0.6);
    box-shadow: 2px 3px 8px rgba(0,0,0,0.3);
    padding: 8px;
    display: flex;
    transition: box-shadow 0.12s ease;
}
.starter-tile:active .starter-tile-card {
    box-shadow: 0px 0px 2px rgba(0,0,0,0.3);
}
.starter-tile-img {
    width: 100%;
    height: 100%;
}
/* šedivá silueta - "brightness(0)" nejdřív převede každý neprůhledný pixel
   na čistou černou (průhledné pozadí spritu zůstane průhledné), "invert(0.4)"
   pak tu černou rovnoměrně zesvětlí na rgb(102,102,102) (60% černá/40% bílá). */
.starter-tile-img--silhouette {
    filter: brightness(0) invert(0.4);
}
.starter-tile-random-mark {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 40px;
    font-weight: 900;
    color: #fff;
    text-shadow: 1px 1px 3px rgba(0,0,0,0.7), -1px -1px 3px rgba(0,0,0,0.7);
    pointer-events: none;
}
/* Vizuál okopírován z PokemonManager.vue ("pokemon detail" napříč appkou) -
   jméno ve světlém poloprůsvitném boxu s ČERNÝM textem, NE tmavá pilulka
   s bílým textem jako ostatní next/ titulky - proto vlastní třída. */
.starter-tile-name {
    margin-top: 6px;
    display: inline-block;
    background: rgba(255,255,255,0.63);
    color: #000;
    font-size: 16px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 5px;
    text-align: center;
    line-height: 1.2;
    /* jména jako "Charmander"/"Bulbasaur" se na užší kartě nevejdou na šířku
       sloupce - radši necháme box roztáhnout širší než sloupec (text-center
       na rodiči ho vystředí) než zalomit/oříznout uprostřed slova */
    white-space: nowrap;
}
.starting-welcome-text {
    font-size: 15px;
}
.starting-welcome-oak {
    margin: 0 auto 12px auto;
}
.starting-welcome-text li {
    margin-bottom: 15px;
}
.starting-welcome-actions {
    /* místo pro plovoucí oranžový křížek (ResponsiveDialog), stejné jako
       u ostatních next/ popupů - ať tlačítko "Continue" není křížkem překryté */
    padding-bottom: var(--responsive-dialog-close-clearance);
}
</style>
